import { DateTime } from "luxon";

// starts with oldest date first
const sortByOldestDate = (array, key) =>
  array?.sort(
    (a, b) =>
      DateTime.fromISO(a[key], { zone: "utc" }).toMillis() -
      DateTime.fromISO(b[key], { zone: "utc" }).toMillis()
  );

// starts with newest date first
const sortByDate = (array, key) =>
  array?.sort(
    (a, b) =>
      DateTime.fromISO(b[key], { zone: "utc" }).toMillis() -
      DateTime.fromISO(a[key], { zone: "utc" }).toMillis()
  );

// Convert UTC date from backend to YYYY-MM-DD format
const getLocalDate = (utcString, timezone = DateTime.local().zoneName) => {
  const utcDateTime = DateTime.fromISO(utcString, { zone: "utc" });
  const localDateTime = utcDateTime.setZone(timezone);
  return localDateTime.toFormat("yyyy-MM-dd");
};

// Convert UTC date from backend to YYYY-MM-DD HH:mm:ss ZZZZ format
const getLocalDateTime = (utcString, timezone = DateTime.local().zoneName) => {
  const utcDateTime = DateTime.fromISO(utcString, { zone: "utc" });
  const localDateTime = utcDateTime.setZone(timezone);
  return localDateTime.toFormat("yyyy-MM-dd HH:mm:ss");
};

// Convert UTC date from backend to calendar date and hours/minutes with timezone abbreviation
const getLocalDateTimeWithZone = (
  utcString,
  timezone = DateTime.local().zoneName
) => {
  const utcDateTime = DateTime.fromISO(utcString, { zone: "utc" });
  const localDateTime = utcDateTime.setZone(timezone);
  return {
    date: localDateTime.toFormat("yyyy-MM-dd"),
    time: localDateTime.toFormat("HH:mm (ZZZZ)"),
  };
};

// This is to list all timezone abbreviations
const getAllTimeZoneInfo = () => {
  const timeZones = Intl.supportedValuesOf("timeZone");

  return timeZones.map((tz) => {
    const offset = new Intl.DateTimeFormat("en", {
      timeZone: tz,
      timeZoneName: "longOffset",
    })
      .formatToParts()
      .find((part) => part.type === "timeZoneName").value;
    return { name: `${tz} (${offset})` };
  });
};

// Get start/end times in UTC dates to send to the backend
const getUtcStartEndDate = (days, timezone = DateTime.local().zoneName) => {
  const localTime = DateTime.now().setZone(timezone);

  // For the start date, get 00:00:00 in local time
  // Subtract the input number of days
  // Then convert to UTC time
  const localMidnight = localTime.startOf("day");
  const startOfUTC = localMidnight.minus({ days }).toUTC();

  // For the end date, get 23:59:59 in local time
  // Then convert to UTC time
  const endOfUTC = localTime.endOf("day").toUTC();

  return { startOfUTC, endOfUTC };
};

const calculateYearDifference = (utcString) => {
  const todaysDate = DateTime.now().setZone("utc");
  const pastDate = DateTime.fromISO(utcString, { zone: "utc" });

  // if the past date is invalid, return null
  if (!pastDate.isValid) {
    return null;
  }

  // get the difference between the past year and current year
  const difference = todaysDate.diff(pastDate, "years");

  // return the value as an integer
  return Math.floor(difference.toObject().years);
};

// Checks if a date is in the format YYYY-MM-DD
const isValidDateFormat = (dateString) => {
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  return dateRegex.test(dateString);
};

const calculateAgeFromExamDate = (startDate, endDate) => {
  const start = DateTime.fromISO(startDate, { zone: "utc" });
  const end = DateTime.fromISO(endDate, { zone: "utc" });

  // if the dates are invalid, return a placeholder string
  if (!end.isValid || !start.isValid) {
    return "-";
  }

  // get the difference between the past year and current year
  const difference = end.diff(start, "years");

  // return the value as an integer
  return Math.floor(difference.toObject().years);
};

export {
  sortByOldestDate,
  sortByDate,
  getLocalDate,
  getLocalDateTime,
  getLocalDateTimeWithZone,
  getAllTimeZoneInfo,
  getUtcStartEndDate,
  calculateYearDifference,
  calculateAgeFromExamDate,
  isValidDateFormat,
};
